import { useState } from 'react'
import Sheet from './Sheet'
import { ELEMENTS, RARITY_CONFIG } from '../../types'
import type { Creature } from '../../types'
import BondingModal from '../battle/BondingModal'
import { useToast } from './Toast'

interface CreatureDetailProps {
  creature: Creature | null
  onClose: () => void
  onBond?: (creature: Creature) => void
}

export default function CreatureDetail({ creature, onClose }: CreatureDetailProps) {
  const [bondTarget, setBondTarget] = useState<Creature | null>(null)
  const { success } = useToast()

  if (!creature) return null

  const el = ELEMENTS[creature.element]
  const rarity = RARITY_CONFIG[creature.rarity]

  return (
    <>
    <Sheet open={!!creature} onClose={onClose} side="bottom" title={creature.name}>
      <div className="space-y-5">

        {/* Element + rarity */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`element-pill element-${creature.element}`}>
            {el.emoji} {el.label}
          </span>
          <span className="font-display font-bold text-xs px-2 py-1 rounded-ab border border-white border-opacity-10"
                style={{ color: rarity.color }}>
            {rarity.label}
          </span>
          {creature.isFoundingExclusive && (
            <span className="element-pill element-aether text-xs">✨ Exclusive</span>
          )}
          {creature.isSoulbound && (
            <span className="text-label text-xs">🔒 Soulbound</span>
          )}
        </div>

        {/* Flavour */}
        <p className="text-body text-sm italic opacity-70 leading-relaxed">
          "{creature.description}"
        </p>

        {/* Stats grid */}
        <div>
          <p className="text-label text-xs mb-3">Base Stats</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Attack',  value: creature.stats.attack,  color: '#FF4800' },
              { label: 'Defense', value: creature.stats.defense, color: '#00F0FF' },
              { label: 'Speed',   value: creature.stats.speed,   color: '#39FF14' },
              { label: 'Bond',    value: creature.stats.bond,    color: el.color },
            ].map(stat => (
              <div key={stat.label} className="card-base p-3">
                <div className="flex justify-between items-center mb-1.5">
                  <p className="text-label text-xs">{stat.label}</p>
                  <p className="font-display font-black text-sm" style={{ color: stat.color }}>
                    {stat.value}
                  </p>
                </div>
                <div className="stat-bar">
                  <div
                    className="stat-bar-fill"
                    style={{
                      width: `${(stat.value / 100) * 100}%`,
                      background: `linear-gradient(90deg, ${stat.color}60, ${stat.color})`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Evolution */}
        <div className="card-base p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-label text-xs">Evolution Stage</p>
            <p className="font-display font-bold text-xs" style={{ color: el.color }}>
              Stage {creature.evolutionStage} / 3
            </p>
          </div>
          <div className="flex gap-3">
            {[1, 2, 3].map(stage => (
              <div
                key={stage}
                className="flex-1 flex flex-col items-center gap-1"
              >
                <div
                  className={`w-full h-2 rounded-full transition-all ${
                    stage <= creature.evolutionStage ? '' : 'bg-white bg-opacity-10'
                  }`}
                  style={stage <= creature.evolutionStage ? {
                    background: `linear-gradient(90deg, ${el.color}60, ${el.color})`,
                    boxShadow: `0 0 8px ${el.color}60`,
                  } : {}}
                />
                <p className="text-label text-xs">{stage === 3 ? 'Apex' : `S${stage}`}</p>
              </div>
            ))}
          </div>
          {creature.evolutionStage === 3 && (
            <p className="text-xs text-ab-text-dim mt-2 text-center">Stage 3 — Soulbound forever</p>
          )}
        </div>

        {/* Bond level */}
        {creature.bondedCharacterId && (
          <div className="card-base p-4">
            <div className="flex justify-between items-center mb-2">
              <p className="text-label text-xs">Bond Level</p>
              <p className="font-display font-bold text-xs" style={{ color: el.color }}>
                {creature.bondLevel} / 100
              </p>
            </div>
            <div className="stat-bar">
              <div
                className="stat-bar-fill"
                style={{
                  width: `${creature.bondLevel}%`,
                  background: `linear-gradient(90deg, ${el.color}60, ${el.color})`,
                }}
              />
            </div>
            <p className="text-label text-xs mt-2">Bonded to character: {creature.bondedCharacterId}</p>
          </div>
        )}

        {/* Condition */}
        <div className="flex items-center justify-between card-base p-4">
          <div>
            <p className="text-label text-xs mb-1">Condition</p>
            <div className="stat-bar w-32">
              <div
                className="stat-bar-fill"
                style={{
                  width: `${creature.condition}%`,
                  background: creature.condition > 60
                    ? 'linear-gradient(90deg, #39FF14, #39FF14)'
                    : 'linear-gradient(90deg, #FF4800, #FFB800)',
                }}
              />
            </div>
          </div>
          <p className="font-display font-black text-lg" style={{ color: el.color }}>
            {creature.condition}<span className="text-xs font-normal text-ab-text-dim">/100</span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          {!creature.isSoulbound && (
            <button
              onClick={() => setBondTarget(creature)}
              className="btn-primary flex-1"
            >
              {creature.bondedCharacterId ? 'Rebond Crew' : 'Bond with Crew'}
            </button>
          )}
          {creature.isTrading && (
            <button className="btn-secondary flex-1">
              View Listing
            </button>
          )}
          {!creature.isTrading && !creature.isSoulbound && (
            <button className="btn-ghost flex-1">
              List for Trade
            </button>
          )}
        </div>

      </div>
    </Sheet>

    <BondingModal
      creature={bondTarget}
      onClose={() => setBondTarget(null)}
      onBondComplete={(_c, char, level) => {
        setBondTarget(null)
        success(`${creature.name} bonded with ${char.name} — Bond level ${level}!`)
      }}
    />
    </>
  )
}
